import React, { useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { Link, useNavigate } from 'react-router-dom'
import { Column } from 'primereact/column'
import { Badge } from 'primereact/badge'
import { DataTableFun } from '../../utils/helpers/dataTableFun'
import useModal from '../../utils/hooks/useModal'
import Modal from '../../components/modal/Modal'
import RolesForm from '../../components/roles/RolesForm'
import useAccess from '../../utils/hooks/useAccess'
import { GROUPS, MODULES } from '../../utils/constants/roles'
import useInputs from '../../utils/hooks/useInputs'
import { rolesPathname, userPathname } from '../../routes/settings/pathname'
import RolesService from '../../services/roles/roles.service'
import useHandlerBasicActions from '../../utils/hooks/useHandlerBasicActions'
import DataTableComp from '../../components/common/data-table/DataTableComp'
import MainCommonView from '../../components/common/MainCommonView'
import ViewButton from '../../components/common/buttons/ViewButton'
import './roles.scss'

const Roles = () => {
  const { formatMessage: msg } = useIntl()
  const navigate = useNavigate()

  const { isShowing, toggle } = useModal()
  const { canAdd, canDelete, canEdit } = useAccess()
  const { inputs, updateInputs, onChangeInputs } = useInputs()

  const dataFacade = useHandlerBasicActions({
    sortFieldInitial: 'role',
    getAll: RolesService.getAll,
    removeAction: RolesService.remove,
    editAction: RolesService.update,
    createAction: RolesService.create,
  })

  const [expandedRows, setExpandedRows] = useState(null)

  const handleCreate = async () => {
    const resp = await RolesService.create(inputs)
    if (resp) {
      navigate(`${rolesPathname.ROLE(resp?.resp?._id)}`)
    }
    updateInputs({})
    toggle()
  }

  const onRowEditComplete = async ({ newData }) => {
    const body = {
      role: newData.role,
    }
    const { _id } = newData
    await dataFacade.edit({ _id, body })
  }

  const closeModal = () => {
    updateInputs({})
    toggle()
  }

  const getNumeberUsers = (rowData) => {
    return <Badge value={rowData?.users?.length || 0} className="bg-orange" />
  }

  const rowExpansionTemplate = (data) => {
    if (data?.users?.length > 0) {
      return (
        <div className="user-template">
          {data?.users.map((x) => (
            <div key={x?.name} className="item-user-template">
              <Link to={userPathname.USER(x._id)}>
                <div className="nameUser">{x?.name}</div>
              </Link>
            </div>
          ))}
        </div>
      )
    }
    return <FormattedMessage id="placeholder.not.users.rol" />
  }

  const goToPathButton = (target) => (
    <ViewButton onClick={() => navigate(`${rolesPathname.ROLE(target._id)}`)} />
  )

  const headerOptions = {
    title: msg({ id: 'roles' }),
    handlerHeaderActions: {
      showBackButton: true,
      secondaryButton: canAdd(MODULES.SETTINGS, GROUPS.ROLES) && toggle,
      secondaryButtonText: 'button.add',
      secondaryButtonIcon: 'pi-plus',
    },
    searchOptions: dataFacade?.searchOptions,
  }

  const pagerOptions = {
    ...dataFacade?.pagerOptions,
    totalRecords: dataFacade?.data?.total,
  }

  const dataTable = (
    <DataTableComp
      loading={dataFacade?.loading}
      tableOptions={{
        className: 'table-without-menu',
        value: dataFacade?.data?.resp,
        dataKey: '_id',
        editMode: 'row',
        onRowEditComplete,
        expandedRows,
        onRowToggle: (e) => setExpandedRows(e.data),
        rowExpansionTemplate,
        ...dataFacade?.sortOptions,
      }}
      columns={[
        <Column style={{ width: '10%' }} field="users" expander="users" />,
        <Column
          style={{ width: '50%' }}
          field="role"
          sortable
          editor={(rowData) => DataTableFun.textEditor(rowData)}
          onCellEditComplete={DataTableFun.onCellEditComplete}
          header={msg({ id: 'role' })}
        />,
        <Column
          field="users"
          header={msg({ id: 'users' })}
          body={(rowData) => getNumeberUsers(rowData)}
        />,
        canDelete && (
          <Column
            className="row-delete"
            align="right"
            body={(rowData) =>
              DataTableFun.buttonDelete(rowData, dataFacade.remove)
            }
          />
        ),
        canEdit && <Column className="row-edit" align="right" rowEditor />,
        <Column
          className="row-view"
          body={(target) => goToPathButton(target)}
        />,
      ]}
    />
  )

  return (
    <div className="roles-container">
      <MainCommonView
        headerOptions={headerOptions}
        componentData={dataTable}
        pagerOptions={pagerOptions}
      />

      <Modal
        isShowing={isShowing}
        hide={closeModal}
        width="90%"
        maxWidth="450px"
        title={msg({ id: 'roles.add.title' })}
        context={
          <RolesForm
            inputs={inputs}
            handleSubmit={handleCreate}
            handleChange={onChangeInputs}
          />
        }
      />
    </div>
  )
}

export default Roles
